import { Mail, MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import { ContactForm } from "../components/contact-form";
import { fadeUp, stagger } from "@/lib/animations";

interface ContactPageProps {
  defaultSubject?: string;
}

const ContactPage = ({ defaultSubject }: ContactPageProps) => {
  usePageSeo(pageSeo.contact);
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="w-full border-b border-neutral-100 bg-gradient-to-b from-white to-stone-50 py-16">
        <motion.div
          className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={fadeUp} className="text-4xl font-bold tracking-tight text-neutral-900">
            Get in touch
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-4 text-lg text-neutral-600">
            Whether you have a project in mind, a question about our services, or just
            want to explore whether we're the right fit — we'd like to hear from you.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Content ── */}
      <section className="w-full py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid gap-12 lg:grid-cols-5"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {/* Contact details */}
            <motion.div variants={fadeUp} className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">Contact details</h2>
                <p className="mt-2 text-sm text-neutral-600">
                  We respond to all enquiries within one business day.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Email</p>
                    <a
                      href="mailto:admin@arsoc.co.zw"
                      className="text-sm text-neutral-600 transition-colors hover:text-primary"
                    >
                      admin@arsoc.co.zw
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Location</p>
                    <p className="text-sm text-neutral-600">150 Nelson Mandela Avenue, Harare, Zimbabwe</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Response time</p>
                    <p className="text-sm text-neutral-600">Within one business day</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-neutral-100 bg-stone-50 p-5">
                <p className="text-sm font-semibold text-neutral-900">What to include</p>
                <ul className="mt-2 space-y-1.5 text-sm text-neutral-600">
                  <li>• A brief description of your business</li>
                  <li>• What you're trying to solve or build</li>
                  <li>• Any timeline or constraints worth knowing</li>
                </ul>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              variants={fadeUp}
              className="lg:col-span-3 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm lg:p-8"
            >
              <h2 className="mb-6 text-xl font-bold text-neutral-900">Send us a message</h2>
              <ContactForm defaultSubject={defaultSubject} />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
